import { isLocalAuthEnabled } from '../../shared/feature-flags';

/*
  Logging in to a backend that checks passwords itself, for development (REACT_APP_ENABLE_LOCAL_AUTH, on in `npm start` only). Such
  a backend issues no token: its /admin/** is authenticated with HTTP Basic, so the password goes with every request.

  The password is held in memory, and in sessionStorage so that a reload of the tab keeps the session; never in the AUTH_DATA of
  localStorage, which outlives the tab. It is forgotten on logout. Imports nothing but the flags, so that RestTemplate can use it
  without a cycle through the store.
*/

const STORAGE_KEY = 'LOCAL_AUTH_BASIC';

/* What the server writes as expiresIn when nothing expires (Long.MAX_VALUE) is no date; a local session lasts a working day. */
export const localSessionSeconds = 8 * 60 * 60;

export const noAccessTokenMessage =
    'The server gave no access token. This application needs a server that checks passwords with Auth0 (the auth0 profile).';

let basic = null;

const readStored = () => {
    try {
        return sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
        return null;
    }
};

const writeStored = value => {
    try {
        if (value) {
            sessionStorage.setItem(STORAGE_KEY, value);
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch (e) {
        // No sessionStorage (blocked, or private mode): the memory copy still serves this page.
    }
};

// btoa takes Latin-1 only, so the text goes in as UTF-8 bytes, which is what the server decodes.
const toBase64 = text =>
    btoa(
        encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (match, hex) =>
            String.fromCharCode(parseInt(hex, 16))
        )
    );

/* A login that is answered without a token, and is meant to be (the flag is on): the server checks passwords itself. */
export const isLocalLogin = loginData =>
    isLocalAuthEnabled() && !(loginData && loginData.accessToken);

/* How long the session lasts, in seconds: what the server says, but never a local session longer than a working day. */
export const loginExpiresInSeconds = loginData =>
    isLocalLogin(loginData)
        ? Math.min(
              Number(loginData.expiresIn) || localSessionSeconds,
              localSessionSeconds
          )
        : loginData.expiresIn;

export const setLocalCredentials = (username, password) => {
    basic = toBase64(`${username}:${password}`);
    writeStored(basic);
};

export const clearLocalCredentials = () => {
    basic = null;
    writeStored(null);
};

const currentBasic = () => basic || readStored();

/* Whether requests carry the password: the flag is on and a local login was made in this tab. */
export const usesLocalAuth = () => isLocalAuthEnabled() && !!currentBasic();

export const localAuthorization = () => `Basic ${currentBasic()}`;
